import { clsx } from 'clsx'
import { PropertyValueMap, TemplateResult, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { CommonSize, HeadingLevel, LabelPlacement } from '@/dap-design-system'
import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import styles from './accordion.scss?inline'

export type AccordionVariant =
  | 'default'
  | 'collapsed'
  | 'clean'
  | 'clean-collapsed'

export interface AccordionEventDetail {
  open: boolean
  item: AccordionBaseElement
}

export type AccordionOpenedEvent = CustomEvent<AccordionEventDetail>
export type AccordionClosedEvent = CustomEvent<AccordionEventDetail>

let accordionCounter = 0

export class AccordionBaseElement extends DdsElement {
  static readonly styles = unsafeCSS(styles)

  private readonly accordionId: string
  private readonly buttonId: string
  private readonly contentId: string

  /** The size of the accordion
   * @type { 'sm' | 'lg' }
   */
  @property({ type: String }) size: CommonSize = 'sm'

  /** The heading level of the accordion
   * @type { 1 | 2 | 3 | 4 | 5 | 6 }
   */
  @property({ type: Number })
  headingLevel: HeadingLevel = 4

  /** Whether the accordion is opened */
  @property({ type: Boolean, reflect: true }) opened = false

  /** Whether the accordion is disabled */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** Whether the accordion is in loading state */
  @property({ type: Boolean, reflect: true }) loading = false

  /** The loading aria label of the accordion */
  @property({ type: String }) loadingAriaLabel?: string

  /** The location of the icon
   * @type { 'left' | 'right' }
   */
  @property({ type: String }) iconLocation: LabelPlacement = 'right'

  /** The variant of the accordion
   * @type { 'default' | 'collapsed' | 'clean' | 'clean-collapsed' }
   */
  @property({ type: String }) variant: AccordionVariant = 'default'

  /** Whether this is the last item in an accordion group */
  @property({ type: Boolean }) lastItem = false

  /** ID of element that describes the accordion */
  @property({ type: String, attribute: 'aria-describedby' })
  ariaDescribedBy?: string

  constructor() {
    super()
    accordionCounter++
    this.accordionId = `accordion-${accordionCounter}`
    this.buttonId = `${this.accordionId}-button`
    this.contentId = `${this.accordionId}-content`
  }

  headingRender() {
    return html`<div></div>`
  }

  contentRender() {
    return html`<div></div>`
  }

  connectedCallback() {
    super.connectedCallback()
  }

  validateProperties(): void {
    if (this.headingLevel < 1 || this.headingLevel > 6) {
      console.warn('headingLevel must be between 1 and 6')
    }

    if (
      !['default', 'collapsed', 'clean', 'clean-collapsed'].includes(
        this.variant,
      )
    ) {
      console.warn('Invalid accordion variant. Using default.')
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this.validateProperties()
  }

  private onChange(): void {
    try {
      if (this.opened) {
        this.emit('dds-opened', {
          open: true,
          item: this,
        } as AccordionEventDetail)
      } else {
        this.emit('dds-closed', {
          open: false,
          item: this,
        } as AccordionEventDetail)
      }
    } catch (error) {
      console.error('Error emitting accordion event:', error)
    }
  }

  private toggle(): void {
    if (this.disabled || this.loading) {
      return
    }

    this.opened = !this.opened
    this.onChange()
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.loading) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggle()
    }
  }

  private handleClick(event: Event): void {
    if (this.disabled || this.loading) {
      event.preventDefault()
      return
    }

    this.toggle()
  }

  private renderButtons() {
    const expandedLabel = this.opened
      ? t('accordion.collapse', 'Collapse section')
      : t('accordion.expand', 'Expand section')

    return html`
      <button
        class="accordion__button"
        type="button"
        part="button"
        id="${this.buttonId}"
        aria-controls="${this.contentId}"
        aria-expanded="${this.opened}"
        aria-disabled="${this.disabled}"
        aria-label="${this.ariaLabel ?? expandedLabel}"
        aria-describedby="${ifDefined(this.ariaDescribedBy)}"
        ?disabled="${this.disabled}"
        @click="${this.handleClick}"
        @keydown="${this.handleKeyDown}">
        ${this.loading
          ? html`<span
              class="accordion__loading"
              aria-label="${this.loadingAriaLabel ?? t('loading')}">
              <dap-ds-icon-loading-spinner
                size="16"></dap-ds-icon-loading-spinner>
            </span>`
          : html`<span
              class="accordion__icon-wrapper"
              aria-hidden="true"
              part="icon-wrapper">
              <slot name="icon-opened" class="accordion__icon-opened">
                <dap-ds-icon
                  exportparts="icon:open-icon, base:open-icon-base"
                  size="${this.size}"
                  name="arrow-up-s-line"
                  class="open-icon"></dap-ds-icon>
              </slot>
              <slot name="icon-closed" class="accordion__icon-closed">
                <dap-ds-icon
                  exportparts="icon:close-icon, base:close-icon-base"
                  size="${this.size}"
                  name="arrow-down-s-line"
                  class="close-icon"></dap-ds-icon>
              </slot>
            </span>`}
        <span part="heading-text" class="accordion__heading-text">
          <slot name="heading"> ${this.headingRender()} </slot>
        </span>
      </button>
    `
  }

  private renderContent() {
    return html`
      <div
        part="content"
        class="accordion__content"
        id="${this.contentId}"
        aria-labelledby="${this.buttonId}"
        role="region"
        aria-hidden="${!this.opened}">
        <div part="content-container" class="accordion-content__container">
          <slot></slot>
          ${this.contentRender()}
        </div>
      </div>
    `
  }

  private renderHeading(): TemplateResult {
    return html`
    <div class="accordion__heading" part="heading">
      <${unsafeStatic(`h${this.headingLevel}`)}
       class="accordion__heading-content">
        ${this.renderButtons()}
      </${unsafeStatic(`h${this.headingLevel}`)}>
  </div>
    `
  }

  renderAccordion() {
    return html`
      <div
        part="base"
        class=${clsx('accordion', {
          'accordion--opened': this.opened,
          'accordion--disabled': this.disabled,
          'accordion--loading': this.loading,
          'accordion--sm': this.size === 'sm',
          'accordion--lg': this.size === 'lg',
          'accordion--icon-left': this.iconLocation === 'left',
          'accordion--icon-right': this.iconLocation === 'right',
          [`accordion--${this.variant}`]: this.variant,
          'accordion--last-item': this.lastItem,
        })}>
        ${this.renderHeading()} ${this.renderContent()}
      </div>
    `
  }
}
